import { translate } from "../../../translations/index";
import { useCallback, useState, useEffect, useContext } from "react";
import SocketContext from "../../../context/socket-context";
import { SocketType } from "../../../context/socket-context";
import {
  Statement,
  Stored,
  StudentOpinion,
  PopulatedVotingSession,
} from "../../../types";
import Tablist from "../../../components/tablist";
import { Link } from "react-router";
import APIclient from "../../../api-client";
import { PartyWithMatchScore } from "../../../api-client/sessions_module";
import { getImageFromS3 } from "../../../utils";

enum TABS {
  STATEMENTS,
  RESULTS,
}

export default function StudentResults({
  votingSession,
  answers,
}: {
  votingSession: PopulatedVotingSession;
  answers: Array<Stored<StudentOpinion>>;
}) {
  const socket = useContext<SocketType>(SocketContext);
  const [currentTab, setCurrentTab] = useState(TABS.STATEMENTS);
  const [results, setResults] = useState<null | Array<PartyWithMatchScore>>(
    null,
  );

  const fetchResults = useCallback(
    async (bool: boolean) => {
      if (bool) {
        const results = await APIclient.sessions.results(votingSession.code);
        setResults(results.sort((r1, r2) => r2.score - r1.score));
      } else {
        setResults(null);
        setCurrentTab(TABS.STATEMENTS);
      }
    },
    [votingSession],
  );

  useEffect(() => {
    fetchResults(votingSession.showResults);
    socket.on("showResults", fetchResults);

    return () => {
      socket.off("showResults", fetchResults);
    };
  }, [votingSession, fetchResults]);

  return (
    <>
      <h1 className="voting-session__title" id="resultsTitle">
        <span className="--highlighted">
          {translate("student.results.title")}
        </span>
      </h1>

      <Tablist
        className="voting-session__tablist"
        ariaLabelledBy="resultsTitle"
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        options={[
          {
            value: TABS.STATEMENTS,
            controls: "resultsByStatement",
            text: translate("student.results.byStatement"),
            className: "voting-session__tab",
          },
          {
            value: TABS.RESULTS,
            controls: "resultsByParty",
            text: translate("student.results.byParty"),
            className: "voting-session__tab",
            disabled: results === null,
          },
        ]}
      />

      {currentTab === TABS.STATEMENTS && (
        <StatementResults
          statements={votingSession.statements}
          answers={answers}
        />
      )}
      {/* TODO: Implement this tab */}
      {currentTab === TABS.RESULTS && results !== null && (
        <PartyResults results={results} />
      )}
    </>
  );
}

/* NOTE: This is only exported for testing purposes */
export function StatementResults({
  statements,
  answers,
}: {
  statements: Array<Stored<Statement>>;
  answers: Array<StudentOpinion>;
}) {
  return (
    <ol className="voting-session__content" id="resultsStatements">
      {statements.map((statement, index) => (
        <StatementWithAnswer
          statement={statement}
          answers={answers}
          index={index}
          key={statement._id}
        />
      ))}
    </ol>
  );
}

/* NOTE: This is only exported for testing purposes */
export function StatementWithAnswer({
  statement,
  answers,
  index,
}: {
  statement: Stored<Statement>;
  answers: Array<StudentOpinion>;
  index: number;
}) {
  const answer = answers.find((a) => a.statementId === statement._id);
  const opinion = statement.opinionOptions.find(
    (o) => o.opinion === answer?.opinion,
  );

  return (
    <li className="statement-result" key={statement._id}>
      <h2 className="statement-result__title">
        <span className="statement-result__index">{index + 1}.</span>
        {statement.description}
      </h2>
      <div className="statement-result__details">
        <span
          className="badge badge--opinion statement-result__badge"
          style={{ "--option-color": opinion?.color || "" }}
        >
          {opinion?.opinion || translate("student.results.notAnswered")}
        </span>
        <Link to={`${index + 1}`} className="statement-result__link link">
          {translate("student.results.openStatement")}
        </Link>
      </div>
    </li>
  );
}

export function PartyResults({
  results,
}: {
  results: Array<PartyWithMatchScore>;
}) {
  return (
    <div className="voting-session__content voting-session__content--compact">
      {results.map(({ party, score }) => (
        <div className="party-result" key={party._id}>
          <details className="party-result__wrapper">
            <summary className="party-result__summary">
              {party.hasLogo && (
                // We don;t need alt text here, as the party name is just below
                <img
                  src={getImageFromS3(`parties/${party._id}`)}
                  alt=""
                  className="party-result__logo"
                />
              )}
              {party.name}

              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentcolor"
                className="party-result__toggle"
              >
                <path d="M12 8.6L6 14.6L7.4 16L12 11.4L16.6 16L18 14.6L12 8.6Z" />
              </svg>
            </summary>
            {party.description}
          </details>
          <div className="party-result__score">
            <progress value={score} max={100} className="party-result__bar" />
            <span className="party-result__percentage">
              {Math.round(score)}%
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
